import { useEffect, useState } from "react";
import { apiFetch, ApiError } from "./api";

export interface TokenPreview {
  email: string;
  user_kind: "operator" | "business_user";
  display_name: string | null;
}

export type TokenPreviewState =
  | { status: "loading" }
  | { status: "ready"; preview: TokenPreview }
  | { status: "invalid"; message: string };

/** Resolves a single-use token (invitation or reset) to its target email so
 *  the form can show the user *which* account they're about to operate on. */
export function useTokenPreview(
  token: string | null,
  purpose: "invitation" | "password_reset",
): TokenPreviewState {
  const [state, setState] = useState<TokenPreviewState>({ status: "loading" });

  useEffect(() => {
    if (!token) {
      setState({ status: "invalid", message: "Missing token." });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const preview = await apiFetch<TokenPreview>(
          `/auth/check-token?token=${encodeURIComponent(token)}&purpose=${purpose}`,
        );
        if (!cancelled) setState({ status: "ready", preview });
      } catch (err) {
        if (cancelled) return;
        const msg =
          err instanceof ApiError
            ? err.message
            : "Could not verify this link.";
        setState({ status: "invalid", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, purpose]);

  return state;
}
